import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import { forwardRef, KeyboardEvent, useImperativeHandle, useState } from 'react';
import { BrowserWebAddIcon, ChevronGrabberVerticalIcon } from '../../../icons';
import { TextField } from '../../atomic/input/textField';

type Props = {
  label: string;
  value: string | null;
  min: string;
  max?: string;
  onChange: (value: string) => void;
};

export type MonthYearPickerHandle = { open: () => void };

const toDate = (month: string) => parseISO(`${month}-01`);

/** A month-and-year field; the label doubles as the placeholder while no month is picked. */
export const MonthYearPicker = forwardRef<MonthYearPickerHandle, Props>(
  ({ label, value, min, max, onChange }, ref) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<Date | null>(null);
    const shown = draft ?? (value ? toDate(value) : null);

    useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);

    const openOnKey = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      setOpen(true);
    };

    return (
      <MuiDatePicker<Date>
        views={['year', 'month']}
        openTo="month"
        inputFormat="MMMM yyyy"
        minDate={toDate(min)}
        maxDate={max ? toDate(max) : undefined}
        value={shown}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setDraft(null);
        }}
        // Picking the year first is an intermediate step; only the month commits.
        onChange={setDraft}
        onAccept={(date) => {
          if (date) onChange(format(date, 'yyyy-MM'));
        }}
        PaperProps={{ className: 'shadow-500' }}
        renderInput={({ inputRef, inputProps }) => (
          <div className="w-[221px]">
            <TextField
              className="h-10 cursor-pointer gap-0 border-grey-100 bg-white px-2"
              inputProps={{
                ref: inputRef,
                ...inputProps,
                'aria-label': label,
                placeholder: label,
                value: shown ? inputProps?.value : '',
                className:
                  'cursor-pointer text-primary-main placeholder:!text-primary-main placeholder:!opacity-100',
                readOnly: true,
                'aria-haspopup': 'dialog',
                onKeyDown: openOnKey
              }}
              startAdornment={<BrowserWebAddIcon className="text-primary-main" />}
              endAdornment={<ChevronGrabberVerticalIcon className="text-primary-main" />}
              onClick={() => setOpen(true)}
            />
          </div>
        )}
      />
    );
  }
);

MonthYearPicker.displayName = 'MonthYearPicker';
